import { useEffect, useRef, useCallback } from 'react';
import { DealerManager } from '@/utils/dealerManager';

interface UseInactivityTimeoutProps {
  isExpanded: boolean;
  onTimeout: () => void;
  timeoutMs?: number;
}

export const useInactivityTimeout = ({ 
  isExpanded, 
  onTimeout, 
  timeoutMs = 10 * 60 * 1000 // 10 minutes default
}: UseInactivityTimeoutProps) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (isExpanded) {
      lastActivityRef.current = Date.now();
      
      timeoutRef.current = setTimeout(() => {
        // Clear dealer cache to force fresh authentication on next interaction
        DealerManager.clearCache();
        
        // Trigger timeout callback
        onTimeout();
      }, timeoutMs);
    }
  }, [isExpanded, onTimeout, timeoutMs]);

  const recordActivity = useCallback(() => {
    if (isExpanded) {
      resetTimeout();
    }
  }, [isExpanded, resetTimeout]);

  // Set up timeout when chat is expanded
  useEffect(() => {
    if (isExpanded) {
      resetTimeout();
    } else {
      // Clear timeout when chat is collapsed
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isExpanded, resetTimeout]);

  // Listen for user activity events
  useEffect(() => {
    if (!isExpanded) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      recordActivity();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      // Clean up event listeners
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [isExpanded, recordActivity]);

  return {
    recordActivity,
    getTimeSinceLastActivity: () => Date.now() - lastActivityRef.current
  };
};